import { StateCreator } from 'zustand';
import { StoreState } from '../pdf-editor.store';
import { IFileInfo } from '@/interfaces/interfaces';
import { produceWithPatches, applyPatches, Patch, enablePatches } from 'immer';
import { createSliceActionsWithGet } from '../utils/createNamedSet';

// Enable Immer patches feature
enablePatches();

const MAX_HISTORY_SIZE = 50;

/**
 * A single history entry containing patches to transform state forward and backward.
 * This is much more memory-efficient than storing full state snapshots.
 */
interface HistoryEntry {
  /** Patches to apply for redo (forward) */
  patches: Patch[];
  /** Patches to apply for undo (backward) */
  inversePatches: Patch[];
}

export interface PlaceholdersHistoryState {
  /** Stack of past changes (for undo) */
  past: HistoryEntry[];
  /** Stack of future changes (for redo) */
  future: HistoryEntry[];
  /** Temporary storage for the previous state before an action */
  _pendingSnapshot: IFileInfo[] | null;
}

export interface PlaceholdersHistoryActions {
  /**
   * Captures the current state before making changes.
   * Must be called before any action that modifies placeholders.
   */
  recordHistory: () => void;
  /**
   * Commits the recorded change by computing patches between
   * the snapshot and current state. Called automatically after state changes.
   */
  commitHistory: () => void;
  /**
   * Undo the last action - applies inverse patches
   */
  undo: () => void;
  /**
   * Redo the last undone action - applies forward patches
   */
  redo: () => void;
  /**
   * Check if undo is available
   */
  canUndo: () => boolean;
  /**
   * Check if redo is available
   */
  canRedo: () => boolean;
  /**
   * Clear all history (useful when loading new data)
   */
  clearHistory: () => void;
}

export interface PlaceholdersHistorySlice {
  history: PlaceholdersHistoryState;
  historyActions: PlaceholdersHistoryActions;
}

const initialHistoryState: PlaceholdersHistoryState = {
  past: [],
  future: [],
  _pendingSnapshot: null,
};

type Slice = StateCreator<
  StoreState,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  PlaceholdersHistorySlice
>;

export const createPlaceholdersHistorySlice: Slice = (set, get) => ({
  history: { ...initialHistoryState },

  historyActions: createSliceActionsWithGet(
    set,
    get,
    'placeholdersHistory',
    (namedSet, get) => ({
      recordHistory: () => {
        const currentFiles = get().placeholders.files;
        if (!currentFiles || currentFiles.length === 0) return;

        namedSet((draft) => {
          draft.placeholdersHistory.history._pendingSnapshot = JSON.parse(
            JSON.stringify(currentFiles),
          );
        });
      },

      commitHistory: () => {
        const { _pendingSnapshot } = get().placeholdersHistory.history;
        const currentFiles = get().placeholders.files;

        if (!_pendingSnapshot) return;

        const [, patches, inversePatches] = produceWithPatches(
          _pendingSnapshot,
          () => JSON.parse(JSON.stringify(currentFiles)),
        );

        if (patches.length === 0) {
          namedSet((draft) => {
            draft.placeholdersHistory.history._pendingSnapshot = null;
          });
          return;
        }

        namedSet((draft) => {
          draft.placeholdersHistory.history.past.push({
            patches,
            inversePatches,
          });

          if (
            draft.placeholdersHistory.history.past.length > MAX_HISTORY_SIZE
          ) {
            draft.placeholdersHistory.history.past.shift();
          }

          draft.placeholdersHistory.history.future = [];
          draft.placeholdersHistory.history._pendingSnapshot = null;
        });
      },

      undo: () => {
        const { past } = get().placeholdersHistory.history;
        const currentFiles = get().placeholders.files;

        if (past.length === 0) return;

        const lastEntry = past[past.length - 1];
        if (!lastEntry) return;

        const restoredFiles = applyPatches(
          currentFiles,
          lastEntry.inversePatches,
        );

        namedSet((draft) => {
          draft.placeholdersHistory.history.past.pop();
          draft.placeholdersHistory.history.future.push(lastEntry);
          draft.placeholders.files = restoredFiles as IFileInfo[];
          draft.placeholders.hasUnsavedChanges = true;
          draft.placeholders.activeField = null;
          draft.placeholders.selectedFieldIds = [];
        });
      },

      redo: () => {
        const { future } = get().placeholdersHistory.history;
        const currentFiles = get().placeholders.files;

        if (future.length === 0) return;

        const nextEntry = future[future.length - 1];
        if (!nextEntry) return;

        const restoredFiles = applyPatches(currentFiles, nextEntry.patches);

        namedSet((draft) => {
          draft.placeholdersHistory.history.future.pop();
          draft.placeholdersHistory.history.past.push(nextEntry);
          draft.placeholders.files = restoredFiles as IFileInfo[];
          draft.placeholders.hasUnsavedChanges = true;
          draft.placeholders.activeField = null;
          draft.placeholders.selectedFieldIds = [];
        });
      },

      canUndo: () => get().placeholdersHistory.history.past.length > 0,

      canRedo: () => get().placeholdersHistory.history.future.length > 0,

      clearHistory: () =>
        namedSet((draft) => {
          draft.placeholdersHistory.history.past = [];
          draft.placeholdersHistory.history.future = [];
          draft.placeholdersHistory.history._pendingSnapshot = null;
        }),
    }),
  ),
});
